import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

type DrawerView = 'list' | 'create' | 'detail';

interface SupportDrawerState {
  isOpen: boolean;
  view: DrawerView;
  selectedTicketKey: string | null;
  openDrawer: (view?: DrawerView) => void;
  closeDrawer: () => void;
  setView: (view: DrawerView) => void;
  openTicketDetail: (ticketKey: string) => void;
}

const SupportDrawerContext = createContext<SupportDrawerState | null>(null);

export function SupportDrawerProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<DrawerView>('list');
  const [selectedTicketKey, setSelectedTicketKey] = useState<string | null>(null);

  const openDrawer = useCallback((v: DrawerView = 'list') => {
    setView(v);
    setIsOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsOpen(false);
  }, []);

  const openTicketDetail = useCallback((ticketKey: string) => {
    setSelectedTicketKey(ticketKey);
    setView('detail');
    setIsOpen(true);
  }, []);

  return (
    <SupportDrawerContext.Provider
      value={{ isOpen, view, selectedTicketKey, openDrawer, closeDrawer, setView, openTicketDetail }}
    >
      {children}
    </SupportDrawerContext.Provider>
  );
}

export function useSupportDrawer() {
  const context = useContext(SupportDrawerContext);
  if (!context) throw new Error('useSupportDrawer must be used within SupportDrawerProvider');
  return context;
}
